import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Grid,
  Card,
  CardContent,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  IconButton,
  Tooltip,
  TablePagination,
  InputAdornment
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Search as SearchIcon,
  Download as DownloadIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

const CustomerMaster = () => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentRecord, setCurrentRecord] = useState({
    cust_code: '',
    cust_name: '',
    full_name: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    pin_code: '',
    phone: '',
    email: '',
    contact_person: ''
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const { api } = useAuth();

  useEffect(() => {
    fetchRecords();
  }, []);

  const fetchRecords = async () => {
    try {
      setLoading(true);
      const response = await api.get('/masters/customers');
      setRecords(response.data.data || []);
    } catch (error) {
      console.error('Error fetching customer records:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setCurrentRecord({
      cust_code: '',
      cust_name: '',
      full_name: '',
      address1: '',
      address2: '',
      city: '',
      state: '',
      pin_code: '',
      phone: '',
      email: '',
      contact_person: ''
    });
    setEditMode(false);
    setOpenDialog(true);
  };

  const handleEdit = (record) => {
    setCurrentRecord(record);
    setEditMode(true);
    setOpenDialog(true);
  };

  const handleView = (record) => {
    setCurrentRecord(record);
    setOpenViewDialog(true);
  };

  const handleSave = async () => {
    try {
      if (editMode) {
        await api.put(`/masters/customers/${currentRecord.cust_code}`, currentRecord);
      } else {
        await api.post('/masters/customers', currentRecord);
      }
      fetchRecords();
      setOpenDialog(false);
    } catch (error) {
      console.error('Error saving customer record:', error);
    }
  };

  const handleExport = () => {
    const csvContent = [
      ['Customer Code', 'Customer Name', 'Full Name', 'City', 'State', 'Phone', 'Email'],
      ...filteredRecords.map(record => [
        record.cust_code,
        record.cust_name,
        record.full_name || '',
        record.city || '',
        record.state || '',
        record.phone || '',
        record.email || ''
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'customer_master.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const filteredRecords = records.filter(record =>
    record.cust_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    record.cust_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    record.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    record.city?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const paginatedRecords = filteredRecords.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <PersonIcon sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h4" gutterBottom color="primary">
            Customer Master
          </Typography>
        </Box>
        <Typography variant="body1" color="textSecondary">
          Customer information and contact details management
        </Typography>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Customers
              </Typography>
              <Typography variant="h4">
                {records.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                With Email
              </Typography>
              <Typography variant="h4" color="success.main">
                {records.filter(r => r.email).length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                With Phone
              </Typography>
              <Typography variant="h4" color="info.main">
                {records.filter(r => r.phone).length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Search Results
              </Typography>
              <Typography variant="h4" color="warning.main">
                {filteredRecords.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Search and Actions */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
        <TextField
          label="Search customers..."
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            )
          }}
          sx={{ flexGrow: 1, maxWidth: 400 }}
        />
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={handleExport}
        >
          Export CSV
        </Button>
      </Box>

      {/* Data Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Customer Code</strong></TableCell>
                <TableCell><strong>Customer Name</strong></TableCell>
                <TableCell><strong>Full Name</strong></TableCell>
                <TableCell><strong>City</strong></TableCell>
                <TableCell><strong>State</strong></TableCell>
                <TableCell><strong>Contact</strong></TableCell>
                <TableCell><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedRecords.map((record) => (
                <TableRow key={record.cust_code} hover>
                  <TableCell>
                    <Chip 
                      label={record.cust_code} 
                      color="primary" 
                      variant="outlined" 
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                      {record.cust_name}
                    </Typography>
                  </TableCell>
                  <TableCell>{record.full_name || '-'}</TableCell>
                  <TableCell>{record.city || '-'}</TableCell>
                  <TableCell>{record.state || '-'}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      {record.phone && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <PhoneIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                          <Typography variant="caption">{record.phone}</Typography>
                        </Box>
                      )}
                      {record.email && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <EmailIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                          <Typography variant="caption">{record.email}</Typography>
                        </Box>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Tooltip title="View Details">
                      <IconButton size="small" onClick={() => handleView(record)}>
                        <ViewIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => handleEdit(record)}>
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={filteredRecords.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(event, newPage) => setPage(newPage)}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
        />
      </Paper>

      {/* Floating Action Button */}
      <Fab
        color="primary"
        aria-label="add"
        sx={{ position: 'fixed', bottom: 16, right: 16 }}
        onClick={handleAdd}
      >
        <AddIcon />
      </Fab>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editMode ? 'Edit Customer' : 'Add New Customer'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Customer Code"
                value={currentRecord.cust_code}
                onChange={(e) => setCurrentRecord({...currentRecord, cust_code: e.target.value})}
                disabled={editMode}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Customer Name"
                value={currentRecord.cust_name}
                onChange={(e) => setCurrentRecord({...currentRecord, cust_name: e.target.value})}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Full Name"
                value={currentRecord.full_name}
                onChange={(e) => setCurrentRecord({...currentRecord, full_name: e.target.value})}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address Line 1"
                value={currentRecord.address1}
                onChange={(e) => setCurrentRecord({...currentRecord, address1: e.target.value})}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address Line 2"
                value={currentRecord.address2}
                onChange={(e) => setCurrentRecord({...currentRecord, address2: e.target.value})}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="City"
                value={currentRecord.city}
                onChange={(e) => setCurrentRecord({...currentRecord, city: e.target.value})}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="State"
                value={currentRecord.state}
                onChange={(e) => setCurrentRecord({...currentRecord, state: e.target.value})}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="PIN Code"
                value={currentRecord.pin_code}
                onChange={(e) => setCurrentRecord({...currentRecord, pin_code: e.target.value})}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone"
                value={currentRecord.phone}
                onChange={(e) => setCurrentRecord({...currentRecord, phone: e.target.value})}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={currentRecord.email}
                onChange={(e) => setCurrentRecord({...currentRecord, email: e.target.value})}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Contact Person"
                value={currentRecord.contact_person}
                onChange={(e) => setCurrentRecord({...currentRecord, contact_person: e.target.value})}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">
            {editMode ? 'Update' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={openViewDialog} onClose={() => setOpenViewDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PersonIcon />
            Customer Details
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" gutterBottom color="primary">
              {currentRecord.cust_name}
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="caption" color="textSecondary">Customer Code</Typography>
                <Typography variant="body2">{currentRecord.cust_code}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="textSecondary">Full Name</Typography>
                <Typography variant="body2">{currentRecord.full_name || '-'}</Typography>
              </Grid>
              
              {currentRecord.address1 && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="textSecondary">Address</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mt: 0.5 }}>
                    <LocationIcon sx={{ fontSize: 16, color: 'text.secondary', mt: 0.2 }} />
                    <Box>
                      <Typography variant="body2">{currentRecord.address1}</Typography>
                      {currentRecord.address2 && (
                        <Typography variant="body2">{currentRecord.address2}</Typography>
                      )}
                      {(currentRecord.city || currentRecord.state || currentRecord.pin_code) && (
                        <Typography variant="body2">
                          {[currentRecord.city, currentRecord.state, currentRecord.pin_code]
                            .filter(Boolean).join(', ')}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Grid>
              )}
              
              {currentRecord.phone && (
                <Grid item xs={6}>
                  <Typography variant="caption" color="textSecondary">Phone</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                    <PhoneIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                    <Typography variant="body2">{currentRecord.phone}</Typography>
                  </Box>
                </Grid>
              )}
              
              {currentRecord.email && (
                <Grid item xs={6}>
                  <Typography variant="caption" color="textSecondary">Email</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                    <EmailIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                    <Typography variant="body2">{currentRecord.email}</Typography>
                  </Box>
                </Grid>
              )}
              
              {currentRecord.contact_person && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="textSecondary">Contact Person</Typography>
                  <Typography variant="body2">{currentRecord.contact_person}</Typography>
                </Grid>
              )}
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenViewDialog(false)}>Close</Button>
          <Button 
            onClick={() => {
              setOpenViewDialog(false);
              handleEdit(currentRecord);
            }} 
            variant="contained"
          >
            Edit
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CustomerMaster;